import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import {ChatContainer, MainContainer, Message, MessageList, TypingIndicator} from "@chatscope/chat-ui-kit-react";
import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {AudioRecorder} from 'react-audio-voice-recorder';
import styles from './css/Speaking.module.css';
import practiceStyles from './css/Practice.module.css';
import {api} from './Api.js';
import WordModalWindow from './WordModalWindow.js';
import AnalizeModalWindow from './AnalizeModalWindow.js';
import WritingRoles from "./WritingRoles.js";
import PreflightLoader, {parseISODuration, usePreflightTimer} from "./PreflightLoader";
import {config} from "./Constants";

const stopWords = new Set([
    "the", "and", "but", "for", "are", "was", "were", "been", "being", "this", "that", "these", "those"
]);

const createUuid = () => {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }

    // UUID v4 fallback for older browsers. The backend requires this format for
    // Idempotency-Key, so a timestamp-like local identifier is not sufficient.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (placeholder) => {
        const random = Math.floor(Math.random() * 16);
        return (placeholder === 'x' ? random : (random & 0x3) | 0x8).toString(16);
    });
};

const getAudioSource = (message) => {
    if (message.audioUrl || message.speechUrl) {
        return message.audioUrl || message.speechUrl;
    }

    return null;
};

const normaliseMessage = (message) => ({
    ...message,
    id: message.id || message.messageId || createUuid(),
    audioSource: getAudioSource(message)
});

const getResponseMessages = (data) => {
    if (Array.isArray(data.messages)) {
        return data.messages.map(normaliseMessage);
    }

    return [data.message || data.assistantMessage].filter(Boolean).map(normaliseMessage);
};

const getHistoryMessages = (data) => {
    if (Array.isArray(data)) {
        return data.map(normaliseMessage);
    }

    return (data.content || data.messages || []).map(normaliseMessage);
};

const Speaking = ({showNotification}) => {
    const [role, setRole] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [sessionError, setSessionError] = useState(false);
    const [messages, setMessages] = useState([]);
    const [phase, setPhase] = useState('idle');
    const [showWordModal, setShowWordModal] = useState(false);
    const [wordModalData, setWordModalData] = useState(null);
    const [analysisMessage, setAnalysisMessage] = useState(null);
    const [showTextMap, setShowTextMap] = useState({});
    // "marin" is the backend's default voice and keeps the create-session
    // payload valid before the server returns its selected voice.
    const [voice, setVoice] = useState('marin');
    const [expectedWaitTime, setExpectedWaitTime] = useState(0);

    const navigate = useNavigate();
    const messageListRef = useRef(null);
    const creatingSessionRef = useRef(false);
    const sendingTurnRef = useRef(false);
    const audioUrlsRef = useRef([]);
    const isBusy = phase !== 'idle';
    const timeLeft = usePreflightTimer(expectedWaitTime, isBusy);

    const scrollToLatest = useCallback(() => {
        messageListRef.current?.scrollToBottom('smooth');
    }, []);

    useLayoutEffect(() => {
        scrollToLatest();
    }, [messages.length, isBusy, scrollToLatest]);

    useEffect(() => () => {
        audioUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    }, []);

    const fetchWaitTime = useCallback(async (endpoint) => {
        if (!config.enablePreflight) {
            return;
        }

        const waitResponse = await api.fetchWaitTime(endpoint, navigate, showNotification);
        if (api.isValidResponse(waitResponse)) {
            setExpectedWaitTime(parseISODuration(waitResponse.data.expectedWaitTime));
        }
    }, [navigate, showNotification]);

    const loadSessionMessages = useCallback(async (currentSessionId) => {
        const allMessages = [];
        let page = 0;

        // The API returns a Spring-style page. Keep the UI independent from the
        // page size so a long, resumed conversation is still complete.
        while (page < 100) {
            const response = await api.getSpeakingMessages(currentSessionId, navigate, showNotification, page);
            if (!api.isValidResponse(response)) {
                return null;
            }

            allMessages.push(...getHistoryMessages(response.data));
            const isLastPage = response.data.last === true
                || (typeof response.data.totalPages === 'number' && page >= response.data.totalPages - 1)
                || (Array.isArray(response.data) && response.data.length < 50);

            if (isLastPage) {
                return allMessages;
            }
            page += 1;
        }

        showNotification('The speaking history is too large to load at once.');
        return null;
    }, [navigate, showNotification]);

    const applySessionResponse = useCallback(async (response) => {
        if (!api.isValidResponse(response) || !response.data.sessionId) {
            setSessionError(true);
            return false;
        }

        setSessionId(response.data.sessionId);
        setVoice(response.data.voice || voice);
        const initialMessages = getResponseMessages(response.data);
        if (initialMessages.length > 0) {
            setMessages(initialMessages);
            return true;
        }

        // A greeting can be persisted even if it was omitted from the create or
        // reset response. The session is invalid for this UI if it is still empty:
        // speaking must always be started by the AI.
        const messagesFromHistory = await loadSessionMessages(response.data.sessionId);
        if (messagesFromHistory?.length) {
            setMessages(messagesFromHistory);
            return true;
        }

        showNotification('The speaking session did not receive the AI opening message.');
        setSessionError(true);
        return false;
    }, [loadSessionMessages, showNotification, voice]);

    const openSession = useCallback(async (selectedRole) => {
        if (creatingSessionRef.current) {
            return;
        }

        creatingSessionRef.current = true;
        setPhase('starting');
        setExpectedWaitTime(0);
        setMessages([]);
        setSessionId(null);
        setSessionError(false);

        try {
            const activeSession = await api.getActiveSpeakingSession(selectedRole.roleId, navigate, showNotification);
            if (api.isValidResponse(activeSession) && activeSession.data.sessionId) {
                setSessionId(activeSession.data.sessionId);
                setVoice(activeSession.data.voice || voice);
                const messagesFromHistory = await loadSessionMessages(activeSession.data.sessionId);
                if (messagesFromHistory?.length) {
                    setMessages(messagesFromHistory);
                } else {
                    showNotification('The saved speaking session is empty. Please reset the scenario.');
                    setSessionError(true);
                }
                return;
            }

            if (activeSession !== null) {
                setSessionError(true);
                return;
            }

            const response = await api.createSpeakingSession({
                roleId: selectedRole.roleId,
                voice
            }, navigate, showNotification);
            await applySessionResponse(response);
        } finally {
            creatingSessionRef.current = false;
            setExpectedWaitTime(0);
            setPhase('idle');
        }
    }, [applySessionResponse, loadSessionMessages, navigate, showNotification, voice]);

    const handleRoleSelected = useCallback((selectedRole) => {
        setRole(selectedRole);
        openSession(selectedRole);
    }, [openSession]);

    const resetSession = useCallback(async () => {
        if (!sessionId || isBusy || !window.confirm('Start this speaking scenario from the beginning? Your current session will be archived.')) {
            return;
        }

        setPhase('resetting');
        setExpectedWaitTime(0);
        setMessages([]);
        setSessionError(false);

        try {
            const response = await api.resetSpeakingSession(sessionId, navigate, showNotification);
            await applySessionResponse(response);
        } finally {
            setExpectedWaitTime(0);
            setPhase('idle');
        }
    }, [applySessionResponse, isBusy, navigate, sessionId, showNotification]);

    const returnToRoleSelection = useCallback(() => {
        if (isBusy) {
            return;
        }

        audioUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        audioUrlsRef.current = [];
        setRole(null);
        setSessionId(null);
        setMessages([]);
        setShowTextMap({});
        setAnalysisMessage(null);
        setShowWordModal(false);
        setSessionError(false);
        setExpectedWaitTime(0);
    }, [isBusy]);

    const transcribe = useCallback(async (audio) => {
        setPhase('transcribing');
        const audioFile = new File([audio], 'voice.webm', {type: audio.type || 'audio/webm'});
        const response = await api.transcription(audioFile, navigate, showNotification);
        return api.isValidResponse(response) ? response.data : null;
    }, [navigate, showNotification]);

    const handleRecordingComplete = useCallback(async (audio) => {
        if (!sessionId || sendingTurnRef.current || isBusy) {
            return;
        }

        if (audio.size > 15 * 1024 * 1024) {
            showNotification('The recording is larger than the 15 MB limit. Please make a shorter recording.');
            return;
        }

        sendingTurnRef.current = true;

        try {
            const transcript = await transcribe(audio);
            if (!transcript) {
                return;
            }

            const audioSource = URL.createObjectURL(audio);
            audioUrlsRef.current.push(audioSource);
            const turnId = createUuid();
            const outgoingMessage = {
                id: turnId,
                type: 'OUTGOING',
                text: transcript,
                audioSource
            };
            setMessages((currentMessages) => [...currentMessages, outgoingMessage]);

            setPhase('responding');
            await fetchWaitTime(`/speaking/sessions/${sessionId}/messages`);
            const response = await api.sendSpeakingTurn(sessionId, {audio, transcript, turnId}, navigate, showNotification);

            if (api.isValidResponse(response)) {
                setVoice(response.data.voice || voice);
                const persistedUserMessage = response.data.userMessage;
                if (persistedUserMessage?.audioUrl || persistedUserMessage?.speechUrl) {
                    URL.revokeObjectURL(audioSource);
                    audioUrlsRef.current = audioUrlsRef.current.filter((url) => url !== audioSource);
                    setMessages((currentMessages) => currentMessages.map((message) => (
                        message.id === turnId
                            ? {...message, audioSource: getAudioSource(persistedUserMessage)}
                            : message
                    )));
                }
                setMessages((currentMessages) => [...currentMessages, ...getResponseMessages(response.data)]);
            }
        } finally {
            sendingTurnRef.current = false;
            setExpectedWaitTime(0);
            setPhase('idle');
        }
    }, [fetchWaitTime, isBusy, navigate, sessionId, showNotification, transcribe, voice]);

    const renderInteractiveText = useCallback((text) => (
        <div className={styles.transcript}>
            {text.split(/(\s+|[.,!?;:]|\b___+\b)/).map((segment, index) => {
                const isWord = /^[a-zA-Zа-яА-ЯёЁčćžšđČĆŽŠĐ]{3,}$/.test(segment);
                if (isWord && !stopWords.has(segment.toLowerCase())) {
                    return (
                        <button
                            className={styles.word}
                            key={index}
                            onClick={() => {
                                setWordModalData({text, wordEn: segment});
                                setShowWordModal(true);
                            }}
                            type="button"
                        >
                            {segment}
                        </button>
                    );
                }

                return <span key={index}>{segment}</span>;
            })}
        </div>
    ), []);

    const toggleText = useCallback((id) => {
        setShowTextMap((currentMap) => ({...currentMap, [id]: !currentMap[id]}));
    }, []);

    if (isBusy && messages.length === 0) {
        return <div className="common-preloader-form"><PreflightLoader expectedWaitTime={expectedWaitTime}/></div>;
    }

    if (!role) {
        return <WritingRoles showNotification={showNotification} type="SPEAKING" setRole={handleRoleSelected}/>;
    }

    if (sessionError) {
        return (
            <main className={practiceStyles.page}>
                <section className={practiceStyles.errorState} aria-live="polite">
                    <i className="material-icons">mic_off</i>
                    <h2>Speaking session is unavailable</h2>
                    <p>We could not start this scenario. Your existing progress has not been changed.</p>
                    <button className="btn red darken-1" onClick={() => openSession(role)} type="button">
                        Try again
                    </button>
                </section>
            </main>
        );
    }

    return (
        <main className={practiceStyles.page}>
            {showWordModal && (
                <div className={styles.modalOverlay}>
                    <WordModalWindow
                        wordEn={wordModalData.wordEn}
                        text={wordModalData.text}
                        onClose={() => setShowWordModal(false)}
                        showNotification={showNotification}
                        navigate={navigate}
                    />
                </div>
            )}

            {analysisMessage && (
                <div className={styles.modalOverlay}>
                    <AnalizeModalWindow
                        key={analysisMessage.id}
                        speaking={true}
                        text={analysisMessage.text}
                        comment={null}
                        onClose={() => setAnalysisMessage(null)}
                        showNotification={showNotification}
                        navigate={navigate}
                    />
                </div>
            )}

            <div className="row">
                <div className="col s12 m10 offset-m1 l8 offset-l2">
                    <div className={styles.speakingCard}>
                        <div className={styles.header}>
                            <button
                                className="btn-flat white-text"
                                disabled={isBusy}
                                onClick={returnToRoleSelection}
                                type="button"
                                aria-label="Back to role selection"
                            >
                                <i className="material-icons">arrow_back</i>
                            </button>
                            <div className={styles.headerTitle}>
                                <strong>{role.summary}</strong>
                                <span>Speaking practice · record your answer when you are ready</span>
                            </div>
                            <button
                                className={`btn-flat white-text ${styles.resetButton}`}
                                disabled={isBusy}
                                onClick={resetSession}
                                title="Start this scenario from the beginning"
                                type="button"
                            >
                                <i className="material-icons">restart_alt</i>
                                Start over
                            </button>
                        </div>

                        <div className={styles.chatArea}>
                            <MainContainer className={styles.chatContainer}>
                                <ChatContainer>
                                    <MessageList
                                        ref={messageListRef}
                                        autoScrollToBottom={true}
                                        autoScrollToBottomOnMount={true}
                                        scrollBehavior="smooth"
                                        typingIndicator={isBusy ? (
                                            <div className={styles.typingIndicator}>
                                                <TypingIndicator content={phase === 'transcribing'
                                                    ? 'Transcribing your message...'
                                                    : expectedWaitTime > 0
                                                        ? `AI is thinking... ${timeLeft.toFixed(1)}s`
                                                        : 'AI is thinking...'}/>
                                                {expectedWaitTime > 0 && <PreflightLoader expectedWaitTime={expectedWaitTime} compact hideText/>}
                                            </div>
                                        ) : null}
                                    >
                                        {messages.map((message) => (
                                            <Message
                                                key={message.id}
                                                model={{
                                                    type: 'custom',
                                                    direction: message.type?.toLowerCase() === 'incoming' ? 'incoming' : 'outgoing',
                                                    position: 'single'
                                                }}
                                            >
                                                <Message.CustomContent>
                                                    {message.audioSource && (
                                                        <div className={styles.audio}>
                                                            <audio controls onLoadedMetadata={scrollToLatest}>
                                                                <source src={message.audioSource}/>
                                                            </audio>
                                                        </div>
                                                    )}
                                                    {showTextMap[message.id] && renderInteractiveText(message.text)}
                                                    <div className={styles.messageActions}>
                                                        <button className="btn-flat btn-small blue-text" onClick={() => toggleText(message.id)} type="button">
                                                            <i className="material-icons">{showTextMap[message.id] ? 'visibility_off' : 'visibility'}</i>
                                                            {showTextMap[message.id] ? 'Hide transcript' : 'View transcript'}
                                                        </button>
                                                        {message.type === 'OUTGOING' && (
                                                            <button
                                                                className="btn-flat btn-small orange-text text-darken-2"
                                                                onClick={() => setAnalysisMessage(message)}
                                                                type="button"
                                                            >
                                                                <i className="material-icons">analytics</i>
                                                                Review speaking
                                                            </button>
                                                        )}
                                                    </div>
                                                </Message.CustomContent>
                                            </Message>
                                        ))}
                                    </MessageList>
                                </ChatContainer>
                            </MainContainer>
                        </div>

                        <div className={`${styles.recorder} grey lighten-4 ${isBusy ? styles.recorderDisabled : ''}`}>
                            <AudioRecorder
                                onRecordingComplete={handleRecordingComplete}
                                onNotAllowedOrFound={() => showNotification('Check your microphone')}
                                audioTrackConstraints={{noiseSuppression: true, echoCancellation: true}}
                                showVisualizer={true}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default Speaking;
